import { DroneEntity, DroneStatusType } from '@src/core/features';

export type DronesPageCollapseStatus = Extract<
  DroneStatusType,
  'IDLE' | 'MISSION' | 'UPDATE_SOFTWARE_REQUIRED'
>;

export const isUpdateRequiredStatus = (status: DroneStatusType): boolean =>
  status === DroneStatusType.UPDATE_SOFTWARE_REQUIRED;

/**
 * Single source of truth for the UPDATE REQUIRED category. Both signals mean the same thing and
 * may arrive independently: the API can report the operational status directly, or only flag the
 * firmware verdict on an otherwise IDLE/OFFLINE drone.
 */
export const isUpdateRequiredDrone = (drone: DroneEntity): boolean =>
  isUpdateRequiredStatus(drone.status) || drone.ota?.updateRequired === true;

/** Statuses the page can render at all — everything else belongs to Fleet. */
const isOperationalStatus = (drone: DroneEntity): boolean =>
  drone.status === DroneStatusType.IDLE ||
  drone.status === DroneStatusType.MISSION ||
  isUpdateRequiredStatus(drone.status);

/** IDLE in API while order/mission is active (create → takeoff) or optimistic bridge after takeoff. */
export const isDroneInMissionPipeline = (
  drone: DroneEntity,
  optimisticMissionSerials?: string[],
  endedMissionSerials?: string[],
): boolean => {
  // Mission just ended for this serial — force idle until REST reflects it, even if the list
  // still returns MISSION/currentOrderId (lag), so the route does not revive.
  if (endedMissionSerials?.includes(drone.serialNumber)) return false;
  // A fresh optimistic create/takeoff for this serial always shows as on-mission.
  if (optimisticMissionSerials?.includes(drone.serialNumber)) return true;
  if (drone.status === DroneStatusType.MISSION) return true;
  const orderId = drone.currentOrderId;
  return orderId != null && Number(orderId) !== 0;
};

export const partitionDronesPageDrones = (
  drones: DroneEntity[],
  optimisticMissionSerials?: string[],
  endedMissionSerials?: string[],
) => {
  const inMissionPipeline = (drone: DroneEntity) =>
    isDroneInMissionPipeline(drone, optimisticMissionSerials, endedMissionSerials);

  // Spans the FULL list: the firmware need is independent of operational status, so an offline /
  // not-active drone that requires an update still belongs in this category.
  const updateRequiredDrones = drones.filter(isUpdateRequiredDrone);

  // A grounded drone that needs firmware isn't flyable — it lives only in the UPDATE REQUIRED
  // category (which links to Fleet) and stays out of the idle list, carousel and map. One that
  // needs firmware while already flying stays visible: the operator must still watch and land it.
  const visibleDrones = drones.filter(
    (d) => isOperationalStatus(d) && (!isUpdateRequiredDrone(d) || inMissionPipeline(d)),
  );

  return {
    visibleDrones,
    updateRequiredDrones,
    idleDrones: visibleDrones.filter((d) => !inMissionPipeline(d)),
    missionDrones: visibleDrones.filter(inMissionPipeline),
  };
};
